import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { ConfirmationModal } from "@/components/ConfirmationModal";
import type { TranslationKey } from "@/constants/i18n";
import { api, errorMessage, ifMatch } from "@/services/api";
import { useCan } from "@/utils/permissions";

/**
 * Which permission model governs a reason of this kind.
 *
 * The two Notes tabs own their kinds outright — `endorsement` and the two
 * `complimentary_*` sign-offs appear on no other screen — so the API asks for
 * the tab's codename rather than the blanket `__reason` that also covers card
 * replacement, blacklist and cancellation. Gate the buttons the same way, or a
 * tab offers an Add whose request comes back 403.
 */
const MODEL_FOR_KIND: Partial<Record<ReasonKind, string>> = {
  endorsement: "notes_endorsement",
  complimentary_endorsed_by: "notes_complimentary",
  complimentary_approved_by: "notes_complimentary",
};

const modelFor = (kind: ReasonKind) => MODEL_FOR_KIND[kind] ?? "reason";
import { useTranslation } from "@/store/locale";
import { EditDialog, FormPanel } from "@/components/resource-page";
import { Badge } from "@/components/ui/badge";
import { Modal } from "@/components/ui/dialog";
import { AddButton, ListSection } from "@/components/ui/list-section";
import { Loading } from "@/components/ui/loading";
import { SkeletonLines } from "@/components/ui/skeleton";
import { useRecordParam } from "@/hooks/use-record-param";
import { listResource } from "@/utils/geo";
import { cn } from "@/utils/utils";

/**
 * Configurable option lists: one table of (kind, text) rows behind every
 * picker the design calls a "reason" — amendment reasons, note reasons, and
 * the complimentary sign-off names. Each screen picks which kinds it shows and
 * what its dialog calls things.
 */

export type ReasonKind =
  | "card_replacement"
  | "blacklist"
  | "cancellation"
  | "endorsement"
  | "complimentary_endorsed_by"
  | "complimentary_approved_by"
  // The two an endorsement note asks for once its reason is chosen. Both were
  // lists written into the code until they were configured here.
  | "left_card_at"
  | "complaint";

export interface Reason {
  id: number;
  kind: ReasonKind;
  reason: string;
  /**
   * One of the options the product ships with, rather than one somebody added.
   *
   * Editable — the wording is the business's to fix, and a typo in a shipped
   * option is not a reason to make it permanent — but not deletable: the
   * pickers are written expecting these, and a list that can be emptied is a
   * screen that can be broken from the settings page. The delete route refuses
   * too; this is so the row says so before anybody tries.
   *
   * Optional because a row cached from before the field existed has no answer,
   * and the safe reading of "no answer" is "an ordinary one" — the server is
   * what actually stops the delete.
   */
  is_default?: boolean;
  /** Sent back as If-Match on a save or a delete, so one over someone else's is refused. */
  updated_at?: string;
}

const LIST_PAGE = 10;

/** What one group's dialog says. */
interface DialogCopy {
  title: TranslationKey;
  subtitle?: TranslationKey;
  fieldLabel: TranslationKey;
  multiline?: boolean;
}

interface GroupDef {
  kind: ReasonKind;
  heading: TranslationKey;
  addLabel: TranslationKey;
  copy: DialogCopy;
  /**
   * Where the form goes. "panel" takes the list's place, which is what the
   * amendment lists do; "modal" is a sheet over the list, leaving the tab
   * strip and the rows behind it where they were. Defaults to "panel".
   */
  dialog?: "modal" | "panel";
}

const AMENDMENT_OPTIONS: {
  value: ReasonKind;
  label: TranslationKey;
  heading: TranslationKey;
}[] = [
  {
    value: "card_replacement",
    label: "reasons.cardReplacement",
    heading: "reasons.cardReplacementHeading",
  },
  { value: "blacklist", label: "reasons.blacklist", heading: "reasons.blacklistHeading" },
  {
    value: "cancellation",
    label: "reasons.cancellation",
    heading: "reasons.cancellationHeading",
  },
];

const AMENDMENT_COPY: DialogCopy = {
  title: "reasons.amendmentReason",
  fieldLabel: "reasons.reason",
  multiline: true,
};

export const AMENDMENT_GROUPS: GroupDef[] = AMENDMENT_OPTIONS.map(({ value, heading }) => ({
  kind: value,
  heading,
  addLabel: "reasons.addReason",
  dialog: "modal",
  copy: AMENDMENT_COPY,
}));

export const ENDORSEMENT_GROUP: GroupDef = {
  kind: "endorsement",
  heading: "reasons.endorsementHeading",
  addLabel: "reasons.addNew",
  dialog: "modal",
  copy: {
    title: "reasons.notesReason",
    subtitle: "reasons.notesReason",
    fieldLabel: "reasons.reason",
  },
};

/**
 * The two follow-ups, configured beside the reasons that ask for them.
 *
 * Choosing "Left Membership Card" on a note asks where the card was left, and
 * "Member Complaint" asks what the complaint is about. Both answers used to
 * come from a list in the code — a CHECK constraint on the note and a
 * hardcoded array in the form — so adding an option meant a deploy.
 *
 * They sit under Endorsement rather than in the Reasons section because that
 * is where the question is asked: somebody wondering why a counter is missing
 * from the picker looks at the tab whose form showed it.
 */
export const ENDORSEMENT_FOLLOWUP_GROUPS: GroupDef[] = [
  {
    kind: "left_card_at",
    heading: "reasons.leftAtHeading",
    addLabel: "reasons.addNew",
    dialog: "modal",
    copy: {
      title: "hist.leftAt",
      subtitle: "reasons.leftAtList",
      fieldLabel: "reasons.place",
    },
  },
  {
    kind: "complaint",
    heading: "reasons.complaintHeading",
    addLabel: "reasons.addNew",
    dialog: "modal",
    copy: {
      title: "hist.complaintReason",
      subtitle: "reasons.complaintList",
      fieldLabel: "reasons.subject",
    },
  },
];

export const COMPLIMENTARY_GROUPS: GroupDef[] = [
  {
    kind: "complimentary_endorsed_by",
    heading: "reasons.endorsedBy",
    addLabel: "reasons.addNew",
    dialog: "modal",
    copy: {
      title: "reasons.endorsedByTitle",
      subtitle: "reasons.endorsedByList",
      fieldLabel: "reasons.name",
    },
  },
  {
    kind: "complimentary_approved_by",
    heading: "reasons.approvedBy",
    addLabel: "reasons.addNew",
    dialog: "modal",
    copy: {
      title: "reasons.approvedByTitle",
      subtitle: "reasons.approvedByList",
      fieldLabel: "reasons.name",
    },
  },
];

/** The Reasons section as designed: one boxed list per amendment scenario. */
export function ReasonsSection() {
  const t = useTranslation();
  // Each list edits in a sheet over the section, so the heading and the
  // neighbouring lists stay where they are — there is no state here to keep.
  return (
    <div>
      <h2 className="text-title-2 text-heading">{t("cfg.reasons")}</h2>
      <p className="text-fg-muted text-callout mt-1 mb-6">{t("reasons.intro")}</p>
      <div className="divide-line divide-y">
        {AMENDMENT_GROUPS.map((group) => (
          <ReasonGroup
            key={group.kind}
            group={group}
            boxed
            className="py-6 first:pt-0 last:pb-0"
          />
        ))}
      </div>
    </div>
  );
}

/** One heading, its add link, and its rows; boxed when the design draws a border. */
export function ReasonGroup({
  group,
  boxed = false,
  className,
  hidden = false,
  onEditingChange,
}: {
  group: GroupDef;
  boxed?: boolean;
  className?: string;
  /** A sibling is being edited, so this one stands aside. */
  hidden?: boolean;
  /** Tells the parent to stand the other groups down while this one edits. */
  onEditingChange?: (editing: boolean) => void;
}) {
  const t = useTranslation();
  const allow = useCan();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<Reason | null>(null);
  // Editing one group takes over the tab: the other lists would otherwise sit
  // under the form, offering to add a reason to a different list entirely.
  // Ten at a time; the next ten when the list is scrolled to its end.
  const reasons = useInfiniteQuery({
    // "paged" because three screens read this list three different ways — a
    // page at a time here, all of it in the amendment sheet, label-and-value in
    // the note form — and a key that says only the kind had them sharing one
    // cache entry and reading each other's shape.
    queryKey: ["/reasons", "paged", group.kind],
    queryFn: ({ pageParam, signal }) =>
      listResource<Reason>(
        "/reasons",
        { kind: group.kind, page: pageParam, size: LIST_PAGE, sort: "id" },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
  });
  const rows = reasons.data?.pages.flatMap((page) => page.items) ?? [];
  // Keyed by kind: the complimentary tab shows two reason lists side by side,
  // and an unqualified parameter would open the form in both.
  const { editing, setEditing, loading } = useRecordParam<Reason>(
    "edit",
    `reason-${group.kind}`,
    "/reasons",
    rows,
  );
  // Reading the record counts as open: otherwise the lists beside this one stay
  // on screen next to the skeleton, still offering to add rows of their own.
  const editingNow = editing !== null || loading;
  // A sheet leaves the page it opened over intact, so only a panel is worth
  // telling the parent about: it is the one the other lists have to stand
  // aside for.
  const asModal = group.dialog === "modal";
  const takesOver = editingNow && !asModal;
  const report = useRef(onEditingChange);
  report.current = onEditingChange;
  useEffect(() => {
    report.current?.(takesOver);
  }, [takesOver]);
  // By prefix: every shape of this list is stale once a reason is edited.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/reasons"] });
  const remove = useMutation({
    mutationFn: (row: { id: number; updated_at?: string }) =>
      api.delete(`/reasons/${row.id}`, ifMatch(row)),
    onSuccess: () => {
      toast.success(t("common.deleted"));
      void invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t("common.couldNotDelete"))),
  });

  if (hidden) return null;

  const formTitle = t("rp.editSingular").replace("{singular}", t(group.copy.title));

  // A record is read when it is opened, so between the click and the answer
  // there is nothing to fill the form with. The same chrome, empty, rather than
  // the list sitting there as though the click had missed.
  const placeholder = (
    <Loading busy subject={t(group.copy.title)}>
      <SkeletonLines rows={3} />
    </Loading>
  );

  // Adding and editing use the form this group asked for. The kind is not a
  // field: it is the list you opened.
  const editor = loading ? (
    asModal ? (
      <Modal open onClose={() => setEditing(null)} title={formTitle}>
        {placeholder}
      </Modal>
    ) : (
      <FormPanel title={formTitle} onClose={() => setEditing(null)} actions={null}>
        {placeholder}
      </FormPanel>
    )
  ) : editing !== null ? (
    <EditDialog
      variant={asModal ? "modal" : "panel"}
      singular={t(group.copy.title)}
      endpoint="/reasons"
      fields={[
        {
          name: "reason",
          label: t(group.copy.fieldLabel),
          required: true,
          type: group.copy.multiline ? "textarea" : "text",
        },
      ]}
      fixed={{ kind: group.kind }}
      initial={editing === "new" || editing === null ? {} : { reason: editing.reason }}
      id={editing === "new" || editing === null ? null : editing.id}
      // The record was read when it was opened; its stamp goes back with the
      // save, which the server refuses outright without one.
      stamp={editing === "new" || editing === null ? null : (editing.updated_at ?? null)}
      onClose={() => setEditing(null)}
      onSaved={() => {
        setEditing(null);
        void queryClient.invalidateQueries({ queryKey: ["/reasons"] });
      }}
    />
  ) : null;

  // A panel stands where the list was; a sheet sits over it, so the list keeps
  // rendering underneath.
  if (editor !== null && !asModal) return <section className={className}>{editor}</section>;

  return (
    <section className={className}>
      {/* No refresh button, like the rest of Configuration: every reason here
          is added, renamed and deleted on this screen, so the list is already
          the newest there is. The keys stay — they are what dims the rows
          while a write's refetch lands. */}
      <ListSection
        title={t(group.heading)}
        shown={rows.length}
        total={reasons.data?.pages[0]?.total}
        refreshKeys={[["/reasons", group.kind]]}
        refresh={false}
        action={
          allow(`add__${modelFor(group.kind)}`) ? (
            <AddButton onClick={() => setEditing("new")}>{t(group.addLabel)}</AddButton>
          ) : undefined
        }
        hasMore={reasons.hasNextPage}
        loadingMore={reasons.isFetchingNextPage}
        pending={reasons.isPending}
        onLoadMore={() => void reasons.fetchNextPage()}
        empty={t("common.nothingYet")}
        listClassName={cn(boxed && "border-line [scrollbar-width:thin] rounded-lg border px-4")}
      >
        {rows.map((row) => (
          <li key={row.id} className="flex items-center justify-between gap-3 py-2.5">
            <span className="flex min-w-0 items-center gap-2">
              <span className="text-fg text-callout min-w-0 break-words">{row.reason}</span>
              {/* Says why there is no Delete beside it, in the row rather than
                  in a message after the attempt. */}
              {row.is_default ? (
                <Badge tone="muted" title={t("reasons.defaultHint")}>
                  {t("reasons.default")}
                </Badge>
              ) : null}
            </span>
            <span className="text-subhead flex shrink-0 items-center gap-1">
              {allow(`change__${modelFor(group.kind)}`) ? (
                <button
                  type="button"
                  onClick={() => setEditing(row)}
                  className="text-fg-muted hover:bg-surface-inset hover:text-fg rounded-lg px-2 py-1 font-medium transition-colors"
                >
                  {t("common.edit")}
                </button>
              ) : null}
              {/* Not for the ones the product ships with. The route refuses
                  them as well — this is so the refusal is not the first anyone
                  hears of it. */}
              {allow(`delete__${modelFor(group.kind)}`) && !row.is_default ? (
                <button
                  type="button"
                  onClick={() => setDeleting(row)}
                  className="text-fg-muted hover:bg-danger-soft hover:text-danger rounded-lg px-2 py-1 font-medium transition-colors"
                >
                  {t("common.delete")}
                </button>
              ) : null}
            </span>
          </li>
        ))}
      </ListSection>
      {editor}
      <ConfirmationModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting);
          setDeleting(null);
        }}
        title={t("common.confirmDeleteTitle").replace("{model}", t(group.copy.title))}
        message={deleting ? t("reasons.confirmDelete").replace("{name}", deleting.reason) : ""}
      />
    </section>
  );
}
